import { useMarketsEnabled } from "module/market/hook/useMarketsEnabled"
import { formatNumberFundingPayment } from "util/format"

import { Badge, Box, HStack, Skeleton, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react"
import { MarketListIcon } from "component/MarketListIcon"
import { Stats } from "component/Stats"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Positions } from "sdk-react/clearingHouse/usePositions"

interface FundingPaymentPendingTableRowProps {
    baseSymbol: string
    pendingFundingPaymentStr: string
}

const FundingPaymentPendingTableRow = ({
    baseSymbol,
    pendingFundingPaymentStr,
}: FundingPaymentPendingTableRowProps) => {
    function getPaymentDirection() {
        if (pendingFundingPaymentStr) {
            if (pendingFundingPaymentStr.startsWith("-")) {
                return "receiving"
            } else {
                return "paying"
            }
        } else {
            return "—"
        }
    }
    const paymentDirection = getPaymentDirection()

    return (
        <Tr>
            <Td>
                <HStack>
                    <Box boxSize="16px">
                        <MarketListIcon coin={baseSymbol} boxSize="16px" />
                    </Box>
                    <Text>{baseSymbol}</Text>
                </HStack>
            </Td>
            <Td>
                {pendingFundingPaymentStr.replace("-", "") || "—"}{" "}
                <Badge colorScheme={paymentDirection === "receiving" ? "green" : "red"}>{paymentDirection}</Badge>
            </Td>
        </Tr>
    )
}

export const FundingPaymentPendingTable = () => {
    const { marketMap } = useMarketsEnabled()
    const { isLoading, totalPendingFundingPayments } = Positions.useContainer()

    const dataList = useMemo(() => {
        if (!marketMap || !totalPendingFundingPayments) {
            return
        }
        return Object.values(marketMap)
            .filter(
                ({ tickerSymbol }) =>
                    totalPendingFundingPayments[tickerSymbol] && !totalPendingFundingPayments[tickerSymbol].eq(0),
            )
            .map(market => ({
                tickerSymbol: market.tickerSymbol,
                baseSymbol: market.baseSymbol,
                pendingFundingPaymentStr: formatNumberFundingPayment(totalPendingFundingPayments[market.tickerSymbol]),
            }))
            .sort((a, b) => a.baseSymbol.localeCompare(b.baseSymbol))
    }, [marketMap, totalPendingFundingPayments])

    const { t } = useTranslation()

    return (
        <Skeleton isLoaded={!isLoading}>
            <Table size="sm" variant="simple">
                <Thead>
                    <Tr>
                        <Th>{t("market.market")}</Th>
                        <Th>{t("history.funding_payment_pending")}</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {dataList?.length === 0 ? (
                        <Tr>
                            <Td colSpan={2}>
                                <Text>{t("history.funding_payment_pending_empty")}</Text>
                            </Td>
                        </Tr>
                    ) : (
                        dataList?.map(({ tickerSymbol, baseSymbol, pendingFundingPaymentStr }) => {
                            return (
                                <FundingPaymentPendingTableRow
                                    key={tickerSymbol}
                                    baseSymbol={baseSymbol}
                                    pendingFundingPaymentStr={pendingFundingPaymentStr}
                                />
                            )
                        })
                    )}
                </Tbody>
            </Table>
        </Skeleton>
    )
}

interface FundingPaymentPendingOfProps {
    symbol: string
}

/**
 * This is used to show pending funding payment on the improved market detail page
 * Pass in a tickerSymbol string (AAVEUSD) to get the pending funding payment of that market
 * @param {FundingPaymentPendingOfProps} { symbol }
 * @return {*}
 */
export const FundingPaymentPendingOf = ({ symbol }: FundingPaymentPendingOfProps) => {
    const { marketMap } = useMarketsEnabled()
    const { totalPendingFundingPayments } = Positions.useContainer()
    const data = useMemo(() => {
        if (!marketMap || !totalPendingFundingPayments) {
            return
        }

        return Object.values(marketMap)
            .filter(({ tickerSymbol }) => tickerSymbol === symbol)
            .map(market => ({
                tickerSymbol: market.tickerSymbol,
                baseSymbol: market.baseSymbol,
                pendingFundingPayment: totalPendingFundingPayments[market.tickerSymbol],
                pendingFundingPaymentStr: formatNumberFundingPayment(totalPendingFundingPayments[market.tickerSymbol]),
            }))
    }, [marketMap, totalPendingFundingPayments, symbol])

    function getPaymentDirection() {
        if (!data) {
            return
        }
        if (data[0].pendingFundingPayment.lt(0)) {
            return "receiving"
        } else {
            return "paying"
        }
    }
    const paymentDirection = getPaymentDirection()

    return (
        <Stats
            showTooltip
            size="xl"
            numberColor="white"
            {...(paymentDirection && {
                helper:
                    paymentDirection === "receiving"
                        ? "You are receiving funding payment from this market. The amount will be credited to your account when the position changes."
                        : "You are paying funding payment to this market. The amount will be deducted from your balance when the position changes.",
                rightIcon: (
                    <Badge colorScheme={paymentDirection === "receiving" ? "green" : "red"}>{paymentDirection}</Badge>
                ),
            })}
            number={data ? data[0].pendingFundingPaymentStr.replace("-", "") : "—"}
            label="Pending Funding Payment"
        />
    )
}
